import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckOutlined, CloseOutlined, CopyOutlined, FileDoneOutlined, LoadingOutlined,
  MessageOutlined, FileWordOutlined, FilePdfOutlined, FileExcelOutlined,
  FilePptOutlined, FileImageOutlined, FileTextOutlined, ReadOutlined,
} from '@ant-design/icons';
import { notification } from 'antd';
import { OfficeTask } from './useOfficeEngine';
import { TheaterState, chatScript, executorName } from './useTheater';
import { PERSONAS } from './office-config';
import { deptOf } from './shared';

/** 任务成果类型 → 徽章（一眼看出口头汇报/Word/PDF/Excel/PPT/图片） */
const resultMeta = (task: OfficeTask): { label: string; cls: string; Icon: React.FC<any> } => {
  if (task.kind === 'design' || task.resultImageUrl) return { label: '图片', cls: 'img', Icon: FileImageOutlined };
  if (task.kind === 'ppt') return { label: 'PPT', cls: 'ppt', Icon: FilePptOutlined };
  const ext = (task.resultFileName || '').toLowerCase();
  const rt = (task.resultType || '').toLowerCase();
  if (rt === 'text' || (!rt && !ext)) return { label: '口头汇报', cls: 'oral', Icon: MessageOutlined };
  if (rt.includes('word') || /\.docx?$/.test(ext)) return { label: 'Word', cls: 'word', Icon: FileWordOutlined };
  if (rt.includes('pdf') || /\.pdf$/.test(ext)) return { label: 'PDF', cls: 'pdf', Icon: FilePdfOutlined };
  if (rt.includes('excel') || rt.includes('xls') || /\.(xlsx?|csv)$/.test(ext)) return { label: 'Excel', cls: 'excel', Icon: FileExcelOutlined };
  if (rt.includes('ppt') || /\.pptx?$/.test(ext)) return { label: 'PPT', cls: 'ppt', Icon: FilePptOutlined };
  if (/\.(png|jpe?g|webp|gif|svg)$/.test(ext)) return { label: '图片', cls: 'img', Icon: FileImageOutlined };
  return { label: '文件', cls: 'text', Icon: FileTextOutlined };
};

const padTimePart = (value: number) => String(value).padStart(2, '0');

/** 今天的消息显示到秒；跨天历史消息补充月日，完整日期放在悬停提示中。 */
const formatChatTime = (timestamp: number) => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '--:--:--';
  const now = new Date();
  const clock = `${padTimePart(date.getHours())}:${padTimePart(date.getMinutes())}:${padTimePart(date.getSeconds())}`;
  const isToday = date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate();
  return isToday ? clock : `${padTimePart(date.getMonth() + 1)}-${padTimePart(date.getDate())} ${clock}`;
};

const fullChatTime = (timestamp: number) => new Date(timestamp).toLocaleString('zh-CN', { hour12: false });

/* ============================================================
   方案五 · 拟真工作群聊过程层（可叠加到任何呈现模式）
   交办后自动弹出"部门任务群"，前端剧本按真实后台节点逐条演出：
   领导下指令 → 经理接令 → @执行人 → 收到马上办 → 进度 → 提交 → 审阅 → 呈报
   ============================================================ */

const ChatLayer: React.FC<{
  tasks: OfficeTask[];
  theater: TheaterState;
  onOpenTask: (localId: string) => void;
}> = ({ tasks, theater, onOpenTask }) => {
  const [open, setOpen] = useState(false);
  const [focusDeptId, setFocusDeptId] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [docsFor, setDocsFor] = useState<OfficeTask | null>(null);   // 知识匹配弹窗
  const listRef = useRef<HTMLDivElement | null>(null);
  const lastTaskIdRef = useRef<string | null>(null);

  const copyMessage = async (text: string, key: string) => {
    try {
      let copied = false;
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          copied = true;
        } catch { /* 使用兼容方案 */ }
      }
      if (!copied) {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        copied = document.execCommand('copy');
        textarea.remove();
      }
      if (!copied) throw new Error('浏览器未允许写入剪贴板');
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1800);
    } catch (error: any) {
      notification.error({
        message: '复制失败',
        description: error?.message || '浏览器未允许访问剪贴板，请直接框选文字后复制。',
        placement: 'topRight',
        duration: 6,
      });
    }
  };

  // 一个部门只保留一个 Tab；最近执行过任务的部门永远排在最左边。
  const deptGroups = useMemo(() => {
    const grouped = new Map<string, OfficeTask[]>();
    for (const task of tasks) {
      const list = grouped.get(task.deptId) || [];
      list.push(task);
      grouped.set(task.deptId, list);
    }
    return Array.from(grouped.entries())
      .map(([deptId, deptTasks]) => ({
        deptId,
        tasks: deptTasks.sort((a, b) => a.startedAt - b.startedAt),
        latestAt: Math.max(...deptTasks.map((task) => task.startedAt)),
      }))
      .sort((a, b) => b.latestAt - a.latestAt);
  }, [tasks]);
  const focusGroup = deptGroups.find((group) => group.deptId === focusDeptId) || deptGroups[0] || null;
  const deptTasks = focusGroup?.tasks || [];

  // 新任务出现 → 自动打开并聚焦到该部门；该部门同时因 latestAt 自动移到最左。
  useEffect(() => {
    const newest = tasks[0];
    if (newest && newest.localId !== lastTaskIdRef.current) {
      lastTaskIdRef.current = newest.localId;
      /* 从会话恢复的历史任务(srv-前缀)不自动弹群聊，只有新交办才弹 */
      if (newest.localId.startsWith('srv-')) return;
      setFocusDeptId(newest.deptId);
      setOpen(true);
    }
  }, [tasks]);

  const taskThreads = deptTasks.map((task) => {
    const beat = theater.beats[task.localId] || 'idle';
    return { task, beat, lines: chatScript(task, task.deptId, beat) };
  });
  const lineCount = taskThreads.reduce((total, thread) => total + thread.lines.length, 0);
  const stateKey = taskThreads.map(({ task, beat, lines }) => `${task.localId}:${beat}:${task.status}:${task.stepText}:${lines.length}`).join('|');

  // 新消息滚到底
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [stateKey, focusGroup?.deptId, open]);

  /* 常驻：没有任务也保留入口（空态提示） */
  const dept = focusGroup ? deptOf(focusGroup.deptId) : null;
  const persona = focusGroup ? PERSONAS[focusGroup.deptId] : null;

  if (!open) {
    const running = tasks.filter((t) => t.status === 'running' || t.status === 'dispatching').length;
    return (
      <button className="aco8-chat-fab" onClick={(e) => { e.stopPropagation(); setOpen(true); }} title="打开任务工作群">
        <MessageOutlined />
        {running > 0 && <em>{running}</em>}
      </button>
    );
  }

  return (
    <div className="aco8-chat" style={dept ? ({ '--acc': dept.accent } as React.CSSProperties) : undefined} onClick={(e) => e.stopPropagation()}>
      <div className="aco8-chat-h">
        <b>{dept ? `${dept.name} · 任务群` : '任务工作群'}</b>
        <span>{persona ? `${persona.manager} · ${deptTasks.length} 项任务 · ${lineCount} 条工作消息` : ''}</span>
        <button className="x" onClick={() => setOpen(false)}><CloseOutlined /></button>
      </div>
      {deptGroups.length > 0 && (
        <div className="aco8-chat-tabs">
          {deptGroups.map((group, index) => {
            const d = deptOf(group.deptId);
            const running = group.tasks.filter((task) => task.status === 'running' || task.status === 'dispatching').length;
            return (
              <button
                key={group.deptId}
                className={`tab ${focusGroup?.deptId === group.deptId ? 'on' : ''} ${index === 0 ? 'latest' : ''}`}
                style={{ '--acc': d.accent } as React.CSSProperties}
                onClick={() => setFocusDeptId(group.deptId)}
              >
                {running > 0 && <LoadingOutlined className="sp" />}
                <span>{d.name}</span>
                <em>{group.tasks.length}</em>
              </button>
            );
          })}
        </div>
      )}
      <div className="aco8-chat-list" ref={listRef}>
        {!focusGroup && (
          <div className="aco8-chat-empty">还没有任务群 · 交办第一件事后，这里会实时演出部门办事的全过程</div>
        )}
        {taskThreads.map(({ task, lines }, taskIndex) => {
          const busy = task.status === 'running' || task.status === 'dispatching';
          return (
            <section className="aco8-task-thread" key={task.localId}>
              <button className="aco8-task-divider" onClick={() => onOpenTask(task.localId)} title="打开任务详情">
                <span>任务 {taskIndex + 1}</span>
                <b>{task.command}</b>
                <em title={fullChatTime(task.startedAt)}>
                  <time dateTime={new Date(task.startedAt).toISOString()}>{formatChatTime(task.startedAt)}</time>
                  {' · '}{busy ? task.stepText : task.status === 'completed' ? '已完成' : '执行失败'}
                </em>
              </button>
              {/* AI 匹配到的知识文件：不占空间，一个按钮弹窗看 */}
              {!!task.matchedDocs?.length && (
                <button
                  className="aco8-docs-btn"
                  onClick={(e) => { e.stopPropagation(); setDocsFor(task); }}
                  title="查看本次匹配到的知识文件"
                >
                  <ReadOutlined /> 匹配知识 <em>{task.matchedDocs.length}</em>
                </button>
              )}
              {lines.map((line, lineIndex) => {
                const messageKey = `${task.localId}-${lineIndex}`;
                return (
                  <div key={messageKey} className={`msg ${line.role}`}>
                    <div className="msg-meta">
                      {line.role !== 'leader' && <span className="who">{line.name}</span>}
                      <time dateTime={new Date(line.timestamp).toISOString()} title={fullChatTime(line.timestamp)}>
                        {formatChatTime(line.timestamp)}
                      </time>
                    </div>
                    <div className="bd">
                      <button
                        type="button"
                        className={`aco8-copy ${copiedKey === messageKey ? 'done' : ''}`}
                        title={copiedKey === messageKey ? '已复制' : '复制这条消息'}
                        aria-label={copiedKey === messageKey ? '已复制' : '复制这条消息'}
                        onClick={(event) => {
                          event.stopPropagation();
                          void copyMessage(line.text, messageKey);
                        }}
                      >
                        {copiedKey === messageKey ? <CheckOutlined /> : <CopyOutlined />}
                      </button>
                      <p>{line.text}</p>
                      {line.attach === 'result' && (() => {
                        const meta = resultMeta(task);
                        return (
                          <button className={`attach type-${meta.cls}`} onClick={() => onOpenTask(task.localId)}>
                            <span className={`aco8-typebadge t-${meta.cls}`}><meta.Icon /> {meta.label}</span>
                            <span className="info">
                              <b>{task.resultFileName || (task.kind === 'ppt' ? 'PPT 设计稿' : task.kind === 'design' ? '设计图' : '任务成果')}</b>
                              <em>点击查看 · {deptOf(task.deptId).name}</em>
                            </span>
                            <FileDoneOutlined className="go" />
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
              {busy && (
                <div className="msg assistant typing">
                  <span className="who">{executorName(task, task.deptId)}</span>
                  <div className="bd"><p><i className="d" /><i className="d" /><i className="d" /></p></div>
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* 知识匹配弹窗：本次任务 AI 检索到的知识文件 */}
      {docsFor && (
        <div className="aco8-docs-pop" onClick={() => setDocsFor(null)}>
          <div className="inner" onClick={(e) => e.stopPropagation()}>
            <div className="h">
              <ReadOutlined />
              <b>AI 匹配到的知识（{docsFor.matchedDocs?.length || 0}）</b>
              <span>本次「{deptOf(docsFor.deptId).name}」检索并参考的知识文件</span>
              <button onClick={() => setDocsFor(null)}><CloseOutlined /></button>
            </div>
            <div className="list">
              {(docsFor.matchedDocs || []).map((d, i) => (
                <div key={`${d.name}-${i}`} className="row">
                  <i className="rk">{i + 1}</i>
                  <FileTextOutlined className="fi" />
                  <b>{d.name}</b>
                  {typeof d.score === 'number' && <em>相关度 {(d.score <= 1 ? d.score * 100 : d.score).toFixed(0)}%</em>}
                </div>
              ))}
              {!docsFor.matchedDocs?.length && <div className="empty">本次未匹配到知识文件</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatLayer;
